/**
 * Perlin Noise-Based Terrain Generation
 * Implements the HEAT Issue Terrain Data-Driven Deformation Playbook.
 *
 * Design contract:
 *   - Mountain HEIGHT  = HEAT score (heat_now)
 *   - Mountain WIDTH   = windowed evidence count (evidence_count_now)
 *   - Perlin/fBm       = ambient base relief only (low amplitude, no analytical meaning)
 *   - Determinism      = same (clientId, window, scope) → same landscape every render
 *   - No mesas         = crest sharpening (pow) + soft tanh cap on stamp sum
 */
import * as THREE from 'three';
import { PerlinNoise } from './perlinNoise';
// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
export interface TerrainConfig {
  width: number;
  height: number;
  widthSegments: number;
  heightSegments: number;
  maxHeight: number;
}
/**
 * IssueStamp — the canonical input for both 2D and 3D terrain deformation.
 * Populated from the /api/issue-terrain endpoint.
 */
export interface IssueStamp {
  id: string;
  /** Stamp centre in normalised coords [-1..1] */
  x: number;
  z: number;
  /** HEAT score 0-100 */
  heatNow: number;
  /** Windowed evidence count (evidence_count_now from API) */
  evidenceCountNow: number;
  /** Sentiment state string */
  sentimentState: 'Positive' | 'Negative' | 'Neutral' | string;
  /** Spillover percentage 0-100 */
  spilloverPercentage: number;
  /** THREE.Color derived from sentimentState */
  color: THREE.Color;
  /** heat_delta from snapshot pipeline — null if no prior snapshot */
  heatDelta: number | null;
}
/** Legacy alias — kept so TerrainCanvas.tsx continues to compile unchanged */
export interface IssueHeightData extends IssueStamp {
  /** Normalised height 0-1 (derived from heatNow) */
  height: number;
}
interface HeightfieldResult {
  /** Float32Array of vertex heights in world units (length = (wSeg+1)*(hSeg+1)) */
  heights: Float32Array;
  min: number;
  max: number;
  /** The seed actually used (for debugging / determinism verification) */
  seed: number;
  /** Grid dimensions */
  cols: number;
  rows: number;
  /** Terrain world dimensions */
  worldW: number;
  worldH: number;
}
// ─────────────────────────────────────────────────────────────────────────────
// Constants (playbook §4, §5)
// ─────────────────────────────────────────────────────────────────────────────
const BASE_AMP   = 0.12;   // §4.1  Perlin base amplitude (keeps data peaks dominant)
const SIGMA_MIN  = 0.07;   // §4.2  Minimum stamp sigma (normalised coords)
const SIGMA_K    = 0.01;   // §4.2  Evidence scaling factor
const SIGMA_MAX  = 0.28;   // §4.2  Clamp upper bound
const CREST_P    = 1.8;    // §4.3  Crest sharpening exponent
const TANH_CAP   = 0.85;   // §4.3  Soft cap for stamp sum (prevents mesas)
const A_MIN      = 0.06;   // §5    Minimum amplitude (normalised)
const A_MAX      = 0.55;   // §5    Maximum amplitude (normalised)
// ─────────────────────────────────────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────────────────────────────────────
/** FNV-1a 32-bit hash — deterministic seed from string */
export function hash32(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
/** Deterministic float in [0,1) from two integers */
function hash01(issueId: string, seed: number): number {
  const combined = `${issueId}:${seed}`;
  return (hash32(combined) % 100000) / 100000;
}
function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
// ─────────────────────────────────────────────────────────────────────────────
// §6  Deterministic peak placement with repulsion
// ─────────────────────────────────────────────────────────────────────────────
interface PlacedStamp {
  id: string;
  nx: number;  // normalised x [-1..1]
  nz: number;  // normalised z [-1..1]
  sigma: number;
}
/**
 * Place issue stamps in normalised [-1..1] space.
 * Rank-ordered along X with deterministic Z jitter, then repulsion passes.
 */
function placeStamps(stamps: IssueStamp[], seed: number): PlacedStamp[] {
  const n = stamps.length;
  if (n === 0) return [];
  const SPACING_X = 0.12;
  const Z_BAND    = 0.55;
  const REPULSION_K = 2.4;
  const REPULSION_ITERS = 5;
  const placed: PlacedStamp[] = stamps.map((s, i) => {
    const e = Math.max(0, s.evidenceCountNow ?? 0);
    const sigma = clamp(SIGMA_MIN + SIGMA_K * Math.sqrt(e), SIGMA_MIN, SIGMA_MAX);
    const nx = (i - (n - 1) / 2) * SPACING_X;
    const nz = lerp(-Z_BAND, Z_BAND, hash01(s.id, seed));
    return { id: s.id, nx, nz, sigma };
  });
  // Repulsion passes
  for (let iter = 0; iter < REPULSION_ITERS; iter++) {
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const dx = placed[j].nx - placed[i].nx;
        const dz = placed[j].nz - placed[i].nz;
        const dist = Math.sqrt(dx * dx + dz * dz);
        const minDist = REPULSION_K * (placed[i].sigma + placed[j].sigma);
        if (dist < minDist && dist > 0) {
          const push = (minDist - dist) / 2;
          const ux = dx / dist;
          const uz = dz / dist;
          placed[i].nx -= ux * push * 0.5;
          placed[i].nz -= uz * push * 0.7;
          placed[j].nx += ux * push * 0.5;
          placed[j].nz += uz * push * 0.7;
        }
      }
    }
  }
  return placed;
}
// ─────────────────────────────────────────────────────────────────────────────
// §3  Core heightfield builder (shared by 2D and 3D)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Build a deterministic heightfield from issue stamps.
 *
 * @param clientId  Used for deterministic seed
 * @param window    e.g. "30d" — used for deterministic seed
 * @param scope     e.g. "combined" — used for deterministic seed
 * @param stamps    Issue stamps with heatNow and evidenceCountNow
 * @param config    Terrain grid dimensions
 * @returns         HeightfieldResult with heights in world units
 */
export function buildHeightfield(
  clientId: string,
  window: string,
  scope: string,
  stamps: IssueStamp[],
  config: TerrainConfig
): HeightfieldResult {
  const seed = hash32(`${clientId}|${window}|${scope}`);
  const noise = new PerlinNoise(seed);
  const cols = config.widthSegments + 1;
  const rows = config.heightSegments + 1;
  const heights = new Float32Array(cols * rows);
  // Place stamps deterministically
  const placed = placeStamps(stamps, seed);
  // Build a lookup map for placed stamps
  const stampMap = new Map<string, PlacedStamp>();
  placed.forEach(p => stampMap.set(p.id, p));

  const worldW = config.width;
  const worldH = config.height;
  const maxH   = config.maxHeight;

  let minH = Infinity, maxHVal = -Infinity;

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      // Normalised coords [-1..1]
      const nx = (col / (cols - 1)) * 2 - 1;
      const nz = (row / (rows - 1)) * 2 - 1;

      // Base Perlin fBm
      const px = (nx + 1) * 4;
      const pz = (nz + 1) * 4;
      let baseH = 0;
      let amp = BASE_AMP, freq = 1;
      for (let oct = 0; oct < 4; oct++) {
        baseH += noise.noise(px * freq, pz * freq) * amp;
        amp *= 0.5; freq *= 2;
      }
      baseH = (baseH + BASE_AMP * 2) * 0.5;

      // Gaussian stamp sum
      let stampSum = 0;
      for (const s of stamps) {
        const p = stampMap.get(s.id);
        if (!p) continue;
        const dx = nx - p.nx;
        const dz = nz - p.nz;
        const d2 = dx * dx + dz * dz;
        if (Math.sqrt(d2) > 4 * p.sigma) continue;
        const h = clamp(s.heatNow, 0, 100) / 100;
        const A = A_MIN + h * (A_MAX - A_MIN);
        const g = Math.exp(-d2 / (2 * p.sigma * p.sigma));
        const sharp = Math.pow(g, CREST_P);
        stampSum += A * sharp;
      }
      // Soft tanh cap
      const capped = Math.tanh(stampSum / TANH_CAP) * TANH_CAP;
      const totalNorm = clamp(baseH + capped, 0, 1);
      const worldY = totalNorm * maxH;

      heights[row * cols + col] = worldY;
      if (worldY < minH)    minH    = worldY;
      if (worldY > maxHVal) maxHVal = worldY;
    }
  }

  return { heights, min: minH, max: maxHVal, seed, cols, rows, worldW, worldH };
}

// ─────────────────────────────────────────────────────────────────────────────
// Terrain geometry builder (used by 3D flat-terrain view)
// ─────────────────────────────────────────────────────────────────────────────
export function buildTerrainGeometry(
  hf: HeightfieldResult,
  stamps: IssueStamp[],
  config: TerrainConfig
): { geometry: THREE.BufferGeometry; material: THREE.Material } {
  const { heights, cols, rows, worldW, worldH } = hf;
  const positions: number[] = [];
  const normals:   number[] = [];
  const uvs:       number[] = [];
  const colors:    number[] = [];
  const indices:   number[] = [];

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = (col / (cols - 1) - 0.5) * worldW;
      const z = (row / (rows - 1) - 0.5) * worldH;
      const y = heights[row * cols + col];
      positions.push(x, y, z);
      normals.push(0, 1, 0);
      uvs.push(col / (cols - 1), row / (rows - 1));

      // Colour by nearest stamp
      const nx = (col / (cols - 1)) * 2 - 1;
      const nz = (row / (rows - 1)) * 2 - 1;
      let bestDist = Infinity, bestColor = new THREE.Color(0x4a6741);
      for (const s of stamps) {
        const dx = nx - s.x;
        const dz = nz - s.z;
        const d = Math.sqrt(dx * dx + dz * dz);
        if (d < bestDist) { bestDist = d; bestColor = s.color; }
      }
      const t = clamp(1 - bestDist * 2, 0, 1);
      const baseColor = new THREE.Color(0x2d4a2d);
      const blended = baseColor.clone().lerp(bestColor, t * 0.6);
      colors.push(blended.r, blended.g, blended.b);
    }
  }

  // Recompute normals
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const idx = row * cols + col;
      const hL = col > 0        ? heights[row * cols + col - 1] : heights[idx];
      const hR = col < cols - 1 ? heights[row * cols + col + 1] : heights[idx];
      const hD = row > 0        ? heights[(row - 1) * cols + col] : heights[idx];
      const hU = row < rows - 1 ? heights[(row + 1) * cols + col] : heights[idx];
      const dx = worldW / (cols - 1);
      const dz = worldH / (rows - 1);
      const nx2 = -(hR - hL) / (2 * dx);
      const nz2 = -(hU - hD) / (2 * dz);
      const len = Math.sqrt(nx2 * nx2 + 1 + nz2 * nz2);
      normals[idx * 3]     = nx2 / len;
      normals[idx * 3 + 1] = 1 / len;
      normals[idx * 3 + 2] = nz2 / len;
    }
  }

  for (let row = 0; row < rows - 1; row++) {
    for (let col = 0; col < cols - 1; col++) {
      const a = row * cols + col;
      const b = a + 1;
      const c = a + cols;
      const d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('normal',   new THREE.Float32BufferAttribute(normals, 3));
  geo.setAttribute('uv',       new THREE.Float32BufferAttribute(uvs, 2));
  geo.setAttribute('color',    new THREE.Float32BufferAttribute(colors, 3));
  geo.setIndex(indices);

  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.85,
    metalness: 0.05,
  });

  const mesh = new THREE.Mesh(geo, mat);
  mesh.castShadow    = true;
  mesh.receiveShadow = true;
  return { geometry: geo, material: mat };
}

// ─────────────────────────────────────────────────────────────────────────────
// §7  Panoramic Mountain Geometry Builder (new for mountain-range scene)
// ─────────────────────────────────────────────────────────────────────────────

export interface MountainGeometryResult {
  geometry: THREE.BufferGeometry;
  /** World-space peak position (summit) */
  peakY: number;
  /** World-space base width (full diameter at ground) */
  baseRadius: number;
}

/**
 * Build a single mountain as a BufferGeometry for one issue.
 *
 * Height  = HEAT score mapped to [A_MIN_WORLD .. A_MAX_WORLD] world units
 * Width   = sqrt(evidenceCountNow) mapped to [R_MIN .. R_MAX] world units
 * Flanks  = Perlin noise for organic irregularity
 * Colors  = vertex colours blending rock-grey at base to sentiment colour at peak
 *
 * @param stamp        The issue data
 * @param seed         Deterministic seed (from hash32 of clientId|window|scope)
 * @param radialSegs   Radial segments around the mountain (default 32)
 * @param heightSegs   Height segments along the mountain (default 24)
 */
export function buildMountainGeometry(
  stamp: IssueStamp,
  seed: number,
  radialSegs = 32,
  heightSegs = 24
): MountainGeometryResult {
  // ── Height mapping ──────────────────────────────────────────────────────────
  // Exaggerated power-curve: HEAT 8→3, HEAT 20→6, HEAT 40→16, HEAT 70→42 world units
  // Uses power 1.7 so high-HEAT issues are dramatically taller than low-HEAT ones.
  // heatNow is treated as 0-100 HEAT score.
  const A_MIN_WORLD = 3;
  const A_MAX_WORLD = 42;
  const hNorm = clamp(stamp.heatNow, 0, 100) / 100;
  const peakY = A_MIN_WORLD + Math.pow(hNorm, 1.7) * (A_MAX_WORLD - A_MIN_WORLD);

  // ── Width mapping ───────────────────────────────────────────────────────────
  // evidence 0→R_MIN=4, evidence 400→R_MAX=18 world units
  const R_MIN = 4;
  const R_MAX = 18;
  const e = Math.max(0, stamp.evidenceCountNow ?? 0);
  const baseRadius = clamp(R_MIN + Math.sqrt(e) * 0.7, R_MIN, R_MAX);

  // ── Perlin noise for flank irregularity ─────────────────────────────────────
  const noise = new PerlinNoise(seed ^ hash32(stamp.id));

  // ── Vertex colours ──────────────────────────────────────────────────────────
  const rockColor = new THREE.Color(0x3a3a4a);   // dark rock at base
  const snowColor = new THREE.Color(0xdde8f0);   // near-white at very top
  const sentColor = stamp.color.clone();          // sentiment at mid-upper

  // ── Build vertices ──────────────────────────────────────────────────────────
  const positions: number[] = [];
  const normals:   number[] = [];
  const colors:    number[] = [];
  const indices:   number[] = [];

  // We build a cone-like surface by iterating height rings from base to apex.
  // At each height level t ∈ [0..1], radius = baseRadius * (1 - t^0.7) with Perlin bump.

  const NOISE_AMP  = 0.50;  // flank noise amplitude (fraction of radius) — increased to survive normal-averaging
  const NOISE_FREQ = 3.4;   // Perlin frequency — slightly higher for more mid-frequency ridging

  // Helper: get vertex index for ring r, segment s
  // Ring 0 = base, ring heightSegs = apex (single point)
  const ringStart: number[] = [];

  // Base centre vertex (index 0)
  positions.push(0, 0, 0);
  normals.push(0, -1, 0);
  colors.push(rockColor.r, rockColor.g, rockColor.b);
  ringStart.push(0); // dummy

  // Rings from base (t=0) to just below apex (t=1)
  for (let ri = 0; ri <= heightSegs; ri++) {
    const t = ri / heightSegs;
    const y = t * peakY;
    // Radius tapers with a slightly concave profile (^0.65 gives steeper flanks)
    const taper = Math.pow(1 - t, 0.65);
    ringStart.push(positions.length / 3);

    for (let si = 0; si < radialSegs; si++) {
      const angle = (si / radialSegs) * Math.PI * 2;
      const cx = Math.cos(angle);
      const cz = Math.sin(angle);

      // Perlin noise bump on radius
      const noiseVal = noise.noise(
        cx * NOISE_FREQ + t * 3.1,
        cz * NOISE_FREQ + t * 2.7
      );
      const r = baseRadius * taper * (1 + noiseVal * NOISE_AMP * (1 - t));

      const x = cx * r;
      const z = cz * r;
      positions.push(x, y, z);

      // Fix #3: Use placeholder normals; computeVertexNormals() will calculate accurate ones from geometry
      // This removes the simplified normal bias that made tall peaks appear textureless
      normals.push(0, 1, 0); // placeholder; will be recalculated

      // Vertex colour: rock at base → sentiment at mid → snow cap at very top
      let col: THREE.Color;
      if (t < 0.55) {
        // base to mid: rock → sentiment
        col = rockColor.clone().lerp(sentColor, t / 0.55);
      } else if (t < 0.85) {
        // mid to upper: sentiment stays
        col = sentColor.clone();
      } else {
        // upper to apex: sentiment → snow
        col = sentColor.clone().lerp(snowColor, (t - 0.85) / 0.15);
      }
      colors.push(col.r, col.g, col.b);
    }
  }

  // Apex vertex
  const apexIdx = positions.length / 3;
  positions.push(0, peakY, 0);
  normals.push(0, 1, 0);
  colors.push(snowColor.r, snowColor.g, snowColor.b);

  // ── Build faces ─────────────────────────────────────────────────────────────

  // Base cap: centre (0) → ring 0 (ringStart[1])
  const baseRingStart = ringStart[1];
  for (let si = 0; si < radialSegs; si++) {
    const a = 0; // base centre
    const b = baseRingStart + si;
    const c = baseRingStart + (si + 1) % radialSegs;
    indices.push(a, c, b); // winding for downward normal
  }

  // Side quads between rings
  for (let ri = 0; ri < heightSegs; ri++) {
    const r0 = ringStart[ri + 1];
    const r1 = ringStart[ri + 2];
    for (let si = 0; si < radialSegs; si++) {
      const a = r0 + si;
      const b = r0 + (si + 1) % radialSegs;
      const c = r1 + si;
      const d = r1 + (si + 1) % radialSegs;
      indices.push(a, b, c);
      indices.push(b, d, c);
    }
  }

  // Apex cap: last ring → apex
  const lastRingStart = ringStart[heightSegs + 1];
  for (let si = 0; si < radialSegs; si++) {
    const a = lastRingStart + si;
    const b = lastRingStart + (si + 1) % radialSegs;
    indices.push(a, b, apexIdx);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('normal',   new THREE.Float32BufferAttribute(normals, 3));
  geo.setAttribute('color',    new THREE.Float32BufferAttribute(colors, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals(); // recompute for smooth shading

  return { geometry: geo, peakY, baseRadius };
}


// ─────────────────────────────────────────────────────────────────────────────
// §8  Ridge Heightfield Builder (continuous terrain beneath mountain range)
// ─────────────────────────────────────────────────────────────────────────────

export interface RidgePeak {
  /** World-space X position of the mountain base */
  wx: number;
  /** World-space Z position of the mountain base */
  wz: number;
  /** Peak height in world units (peakY from buildMountainGeometry) */
  peakY: number;
  /** Base radius in world units (baseRadius from buildMountainGeometry) */
  baseRadius: number;
}

/**
 * Describes a saddle/ridge connection between two peaks.
 * Derived from the API connections[] array using the saddle_weight field.
 */
export interface SaddleEdge {
  /** Index into the peaks array for the first peak */
  indexA: number;
  /** Index into the peaks array for the second peak */
  indexB: number;
  /**
   * Normalised saddle strength 0.0–1.0.
   * 1.0 = strongest shared stakeholder overlap → deepest shared massif.
   * 0.0 = no saddle (isolated peaks).
   */
  saddleWeight: number;
}

export interface RidgeHeightfieldResult {
  /** Flat array of world-space Y values, row-major, (cols × rows) entries */
  heights: Float32Array;
  cols: number;
  rows: number;
  /** Total world width of the grid */
  worldW: number;
  /** Total world depth of the grid */
  worldD: number;
  /** World X of the left edge */
  originX: number;
  /** World Z of the centre line */
  originZ: number;
}

/**
 * Build a continuous ridge heightfield for the mountain range.
 *
 * Each peak contributes a Gaussian foothill:
 *   contribution = peakY * RIDGE_SCALE * exp(-d² / (2 * sigma²))
 * where sigma = baseRadius * SIGMA_FACTOR.
 *
 * A low-amplitude Perlin base layer adds natural undulation between peaks.
 * Heights are capped at RIDGE_CAP_FACTOR * peakY so mountains always
 * emerge clearly above the foothills.
 *
 * @param peaks     Array of RidgePeak descriptors (one per mountain)
 * @param seed      Deterministic seed for Perlin base noise
 * @param colsCount Grid columns (higher = smoother, more expensive)
 * @param rowsCount Grid rows
 * @param margin    Extra world units added on each side of the range
 * @param depth     World depth of the grid (Z extent)
 */
export function buildRidgeHeightfield(
  peaks: RidgePeak[],
  seed: number,
  colsCount   = 120,
  rowsCount   = 40,
  margin      = 30,
  depth       = 80,
  saddleEdges: SaddleEdge[] = []
): RidgeHeightfieldResult {
  // ── Grid extent ─────────────────────────────────────────────────────────────
  const RIDGE_SCALE      = 0.22;   // fraction of peakY used as Gaussian amplitude
  const SIGMA_FACTOR     = 1.4;    // sigma = baseRadius * SIGMA_FACTOR
  const RIDGE_CAP_FACTOR = 0.28;   // max ridge height = 28% of the tallest peak
  const BASE_NOISE_AMP   = 0.8;    // world units of Perlin undulation
  const BASE_NOISE_SCALE = 0.045;  // spatial frequency of base noise

  if (peaks.length === 0) {
    const empty = new Float32Array(colsCount * rowsCount);
    return { heights: empty, cols: colsCount, rows: rowsCount,
             worldW: 100, worldD: depth, originX: -50, originZ: -depth / 2 };
  }

  const minX = Math.min(...peaks.map(p => p.wx)) - margin;
  const maxX = Math.max(...peaks.map(p => p.wx)) + margin;
  const worldW = maxX - minX;
  const originX = minX;
  const originZ = -depth / 2;

  const maxPeakY = Math.max(...peaks.map(p => p.peakY));
  const ridgeCap = maxPeakY * RIDGE_CAP_FACTOR;

  const noise = new PerlinNoise(seed);
  const cols  = colsCount;
  const rows  = rowsCount;
  const heights = new Float32Array(cols * rows);

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const wx = originX + (col / (cols - 1)) * worldW;
      const wz = originZ + (row / (rows - 1)) * depth;

      // Base Perlin layer — gentle undulation across the valley floor
      const baseH = noise.noise(wx * BASE_NOISE_SCALE, wz * BASE_NOISE_SCALE)
                    * BASE_NOISE_AMP;

      // Gaussian contribution from each peak
      let gaussSum = 0;
      for (const p of peaks) {
        const dx = wx - p.wx;
        const dz = wz - p.wz;
        const d2 = dx * dx + dz * dz;
        const sigma = p.baseRadius * SIGMA_FACTOR;
        // Skip vertices more than 3σ away (negligible contribution)
        if (Math.sqrt(d2) > 3 * sigma) continue;
        const g = Math.exp(-d2 / (2 * sigma * sigma));
        gaussSum += p.peakY * RIDGE_SCALE * g;
      }

      // Saddle contribution: each connected pair adds a ridge between peaks.
      // The ridge is a Gaussian centred on the line segment between the two peaks.
      // SADDLE_AMP controls the maximum saddle height as a fraction of the shorter peak.
      // SADDLE_SIGMA_FACTOR controls the width of the connecting ridge.
      const SADDLE_AMP          = 0.35;  // max saddle = 35% of shorter peak height
      const SADDLE_SIGMA_FACTOR = 0.55;  // ridge width = 55% of average base radius
      const SADDLE_CAP_FACTOR   = 0.32;  // saddle never exceeds 32% of shorter peak
      let saddleSum = 0;
      for (const edge of saddleEdges) {
        if (edge.saddleWeight <= 0) continue;
        const pA = peaks[edge.indexA];
        const pB = peaks[edge.indexB];
        if (!pA || !pB) continue;
        // Vector from A to B
        const abx = pB.wx - pA.wx;
        const abz = pB.wz - pA.wz;
        const abLen = Math.sqrt(abx * abx + abz * abz);
        if (abLen < 0.001) continue;
        // Project vertex onto line segment A→B, clamped to [0,1]
        const ax = wx - pA.wx;
        const az = wz - pA.wz;
        const t = Math.max(0, Math.min(1, (ax * abx + az * abz) / (abLen * abLen)));
        // Perpendicular distance from vertex to the line segment
        const closestX = pA.wx + t * abx;
        const closestZ = pA.wz + t * abz;
        const perpDx = wx - closestX;
        const perpDz = wz - closestZ;
        const perpD2 = perpDx * perpDx + perpDz * perpDz;
        // Saddle sigma: proportional to average base radius of the two peaks
        const avgRadius = (pA.baseRadius + pB.baseRadius) * 0.5;
        const saddleSigma = avgRadius * SADDLE_SIGMA_FACTOR;
        if (Math.sqrt(perpD2) > 3 * saddleSigma) continue;
        // Gaussian perpendicular to the ridge line
        const gPerp = Math.exp(-perpD2 / (2 * saddleSigma * saddleSigma));
        // Height envelope along the ridge: peaks at midpoint (t=0.5), tapers to 0 at peaks
        // Using a raised cosine: 0.5 * (1 - cos(2π*t)) → 0 at t=0,1 and 1 at t=0.5
        const gAlong = 0.5 * (1 - Math.cos(2 * Math.PI * t));
        const shorterPeak = Math.min(pA.peakY, pB.peakY);
        const saddleContrib = SADDLE_AMP * edge.saddleWeight * shorterPeak * gPerp * gAlong;
        saddleSum += Math.min(saddleContrib, SADDLE_CAP_FACTOR * shorterPeak);
      }
      // Combine and cap
      const raw = Math.max(0, baseH + gaussSum + saddleSum);
      heights[row * cols + col] = Math.min(raw, ridgeCap);
    }
  }

  return { heights, cols, rows, worldW, worldD: depth, originX, originZ };
}

// ─────────────────────────────────────────────────────────────────────────────
// Transform API peaks → IssueHeightData[]
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Transform the /api/issue-terrain peak objects into IssueHeightData[].
 * Accepts the new API shape (heat_now, evidence_count_now, sentiment_state).
 * Also accepts the legacy decision-board shape (overall_score, posture.score) as fallback.
 */
export const issueToHeightData = (
  issues: Array<{
    id?: string;
    issue_id?: string;
    overall_score?: number;
    heat_now?: number;
    evidence_count_now?: number;
    sentiment_state?: string;
    posture?: { score: number };
    spillover?: { index?: number; percentage?: number };
    spillover_percentage?: number;
  }>
): IssueHeightData[] => {
  const total = issues.length;
  return issues.map((issue, index) => {
    const id       = issue.id ?? issue.issue_id ?? String(index);
    const heatNow  = issue.heat_now ?? issue.overall_score ?? 0;
    const evidenceCountNow = issue.evidence_count_now ?? 0;
    // Sentiment → colour
    const sentimentState = issue.sentiment_state ?? (() => {
      const ps = issue.posture?.score ?? 0;
      if (ps > 0.3)  return 'Positive';
      if (ps < -0.1) return 'Negative';
      return 'Neutral';
    })();
    let color = new THREE.Color(0xA0A0A0);
    if (sentimentState === 'Positive') color = new THREE.Color(0x34C759);
    if (sentimentState === 'Negative') color = new THREE.Color(0xFF3B30);
    const spilloverPercentage =
      issue.spillover_percentage ??
      issue.spillover?.index ??
      issue.spillover?.percentage ??
      0;
    // Normalised height (legacy field, still used by TerrainCanvas colour logic)
    const height = Math.min(heatNow / 75, 1) * 0.8 + 0.2;
    // Golden-spiral placement (legacy fallback for TerrainCanvas)
    const phi   = Math.acos(-1 + (2 * index) / Math.max(total, 1));
    const theta = Math.sqrt(total * Math.PI) * phi;
    const x = Math.cos(theta) * Math.sin(phi);
    const z = Math.sin(theta) * Math.sin(phi);
    return {
      id, x, z, height, color, spilloverPercentage,
      heatNow, evidenceCountNow, sentimentState,
    };
  });
};
/**
 * Legacy: generate heightmap value at a single point.
 * Kept for backward compatibility with TerrainCanvas.
 */
export const generatePerlinHeightmap = (
  x: number,
  z: number,
  issues: IssueHeightData[],
  noise: PerlinNoise,
  scale: number = 0.1
): number => {
  const baseNoise = noise.noise(x * scale, z * scale);
  let height = (baseNoise + 1) * BASE_AMP * 2;
  let amplitude = BASE_AMP; let frequency = 2;
  for (let i = 0; i < 3; i++) {
    height += noise.noise(x * scale * frequency, z * scale * frequency) * amplitude;
    amplitude *= 0.5; frequency *= 2;
  }
  height = (height + 0.5) * 0.4;
  issues.forEach(issue => {
    const dx = x - issue.x;
    const dz = z - issue.z;
    const d2 = dx * dx + dz * dz;
    const e  = Math.max(0, issue.evidenceCountNow ?? 0);
    const sigma = clamp(SIGMA_MIN + SIGMA_K * Math.sqrt(e), SIGMA_MIN, SIGMA_MAX);
    if (Math.sqrt(d2) > 3 * sigma) return;
    const h = clamp(issue.heatNow ?? issue.height * 75, 0, 100) / 100;
    const A = A_MIN + h * (A_MAX - A_MIN);
    const g = Math.exp(-d2 / (2 * sigma * sigma));
    const sharp = Math.pow(g, CREST_P);
    height += A * sharp;
  });
  return Math.max(0, Math.min(1, height));
};
/**
 * Legacy: create terrain from IssueHeightData (used by TerrainCanvas).
 */
export const createPerlinTerrain = (
  issues: IssueHeightData[],
  config: TerrainConfig,
  clientId = 'default',
  window   = '30d',
  scope    = 'combined'
): { geometry: THREE.BufferGeometry; material: THREE.Material } => {
  const hf = buildHeightfield(clientId, window, scope, issues, config);
  return buildTerrainGeometry(hf, issues, config);
};
// ─────────────────────────────────────────────────────────────────────────────
// Lighting + camera helpers (unchanged)
// ─────────────────────────────────────────────────────────────────────────────
export const createTerrainLighting = (): {
  ambient: THREE.Light;
  directional: THREE.Light;
  hemisphere: THREE.Light;
} => {
  const ambient     = new THREE.AmbientLight(0xffffff, 0.5);
  const directional = new THREE.DirectionalLight(0xffffff, 1.0);
  directional.position.set(80, 80, 80);
  directional.castShadow = true;
  directional.shadow.mapSize.width  = 2048;
  directional.shadow.mapSize.height = 2048;
  directional.shadow.camera.left   = -200;
  directional.shadow.camera.right  =  200;
  directional.shadow.camera.top    =  200;
  directional.shadow.camera.bottom = -200;
  directional.shadow.camera.far    =  400;
  const hemisphere  = new THREE.HemisphereLight(0x87CEEB, 0x8B7355, 0.8);
  return { ambient, directional, hemisphere };
};
export const calculateTerrainCameraPosition = (
  terrainWidth: number,
  terrainHeight: number,
  maxTerrainHeight: number
): THREE.Vector3 => {
  const distance = Math.max(terrainWidth, terrainHeight) * 0.95;
  return new THREE.Vector3(
    distance * 0.65,
    maxTerrainHeight * 1.6,
    distance * 0.65
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Legacy alias — createTerrainMesh (used by TerrainCanvas.tsx)
// ─────────────────────────────────────────────────────────────────────────────
/** @deprecated Use createPerlinTerrain instead */
export const createTerrainMesh = createPerlinTerrain;
